import { useCallback, forwardRef, useMemo, useEffect, useRef, useImperativeHandle, MutableRefObject, Ref, useLayoutEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { ColDef, ColumnMovedEvent, ProcessCellForExportParams, RowClassParams, ColumnResizedEvent, ICellRendererParams, GetContextMenuItemsParams } from '@ag-grid-community/core';
import { AgGridReact, CustomNoRowsOverlayProps } from "@ag-grid-community/react";
import { ClipboardModule } from '@ag-grid-enterprise/clipboard';
import { GridChartsModule } from '@ag-grid-enterprise/charts';
import { RowGroupingModule } from '@ag-grid-enterprise/row-grouping';
import { ColumnsToolPanelModule } from '@ag-grid-enterprise/column-tool-panel';
import { ClientSideRowModelModule } from '@ag-grid-community/client-side-row-model';
import { IRichCellEditorParams } from 'ag-grid-community';
import { MenuModule } from "@ag-grid-enterprise/menu";

import { Box, Tooltip, Snackbar, BodySmall, TableHeaderMenuIcon, Portal, Status, BodyXS, AddIcon, ErrorIcon, ToolbarType } from '@parspec/pixel';
import { SectionApiResponseType } from '../queries/apiTypes';
import {
    useUpdateBomDataMutation,
    saveColumnOrderEvents,
    saveColumnWidthEvents,
    useUpdateDatasheetMetaDataMutation,
} from '../queries';
import {
    deleteDialogTitle,
    deleteMessage,
    deleteTitle,
    DELETE_SECTION_TITLE,
    getCancelButtonUrl,
    getRowId,
    TextCellParams,

} from '../shared/utils';
import { setRouteToLocalStorage } from '../../shared/utils/utils';
import DeleteConfirmationDialog from '../../shared/DeleteConfirmationDialog';
import { someThingWentWrongMsg } from '../../shared/constants';
import { Statuses, KIT_ERROR, ROW_LEVEL, BOM_TABLES_ENUM, ROW_HEIGHT } from '../shared/constants';
import MoveSectionDialog from '../shared/MoveSectionDialog';
import ErrorDialog from '../../shared/ErrorDialog';
import { useGetCustomisedColumnData } from '../shared/hooks';
import { MenuOptions } from '../shared/MenuOptions';
import { MenuOptionsContext, TableContext } from '../shared/context';
import CustomToolBarPanel from '../../shared/CustomToolBarPanel';
import { toolBarOptions, BULK_EDIT_OPTIONS } from './helper';
import { defaultColDef } from './helper';
import { LoadingOverlayComponent, NoRowsOverlayWithButtons, QtyTemplate } from '../shared/commonTemplates';
import MoveProductToKitDialog from '../shared/MoveProductToKitDialog';
import { SectionAndBomItemData, SelectedData } from '../shared/types';
import SelectSectionModal from '../shared/SelectSectionModal';
import { aggFunctions, isDisabledCellClassRules, isSectionEditingNotAllowed } from '../pricing&LeadTime/shared/utils';
import AddOptionModal from '../shared/addOptionModal';
import { MainProductDataType } from '../pricing&LeadTime/mainProductTable';
import { useKitPricingUpdate } from '../shared/hooks/useKitPricingUpdate';
import { getTableRightClickOptions } from '../shared/TableRightClickOptions';
import BulkEditModal, { EditOptionsT } from '../pricing&LeadTime/bulkEdit/bulkEditPopup';
import { useBomTableActionHandlers } from '../shared/hooks/useBomTableActionsHandlers';
import { isValidCharactersForMfg } from 'src/features/Settings/Manufacturer/utils';
import { INVALID_CHARACTERS_IN_MFG_NAME } from 'src/features/Settings/Manufacturer/constants';
import AutocompleteEditor from '../shared/AutoCompleteCellEditor';

const modules = [ClientSideRowModelModule, ClipboardModule, GridChartsModule, ColumnsToolPanelModule, RowGroupingModule, MenuModule];
interface RedirectionProps {
    selectionStatus?: Statuses.confirmed | Statuses.notConfirmed;
    id: number;
    type: string;
    annotationStatus?: Statuses;
    docType: string;
    isPagesHidden?: boolean;
    level: boolean
}

interface OmTableProps {
    onPricingInfoEdit: (info: Partial<Omit<MainProductDataType, 'qty' | 'category' | 'manufacturer' | 'model_number' | 'specification' | 'lotInfo'>>) => Promise<void>;
    bomDataLoading?: boolean;
    tableData: SectionAndBomItemData[];
    optionSection?: SectionApiResponseType[];
    isOptionTable?: boolean;
    mainProductOptionsInfo?: Record<number, Array<number>>;
    handleTablesReordering?: (columnOrder: string[]) => void;
    handleTablesWidthChanged?: (targetedColumnId: string, newWidth?: number) => void;
    selectedRowsRef: MutableRefObject<Set<number>>;
    getSectionBodItems?: (sectionId: number, isOptionTable?: boolean) => SectionAndBomItemData[];
    currentGroupWhilePastingRef: React.MutableRefObject<number | null>;
    tableMaxHeight?: number;
    aggregateSelectedItemsFromSections?: (data: SelectedData[], kitId?: number) => void;
    currentTabValue: string | null;
}

const DocumentTable = forwardRef(function DocumentsTable(props: OmTableProps, ref: Ref<AgGridReact | null>) {
    const {
        bomDataLoading,
        tableData,
        optionSection,
        isOptionTable = false,
        mainProductOptionsInfo,
        handleTablesReordering,
        handleTablesWidthChanged,
        selectedRowsRef,
        getSectionBodItems,
        currentGroupWhilePastingRef,
        tableMaxHeight,
        onPricingInfoEdit,
        aggregateSelectedItemsFromSections,
        currentTabValue
    } = props;

    const navigate = useNavigate();

    const { bomId = '0', projectId } = useParams<{ bomId?: string; projectId?: string }>();
    const [searchParams, setSearchParams] = useSearchParams();

    const productId = Number(searchParams.get('productId'));
    const tableRef = useRef<AgGridReact | null>(null);

    const { handleKitPricingUpdate } = useKitPricingUpdate(bomId);
    const { onDuplicate, onAddKitLine, onAddBelow, setSnackbarMessage, onCopyProductDataToKit, onHighlight, onProductMoveOutFromKit, onProductMoveToKit, approveOption, onClickDelete, onClickMoveSection, onAddOption, handleAddOptionForMultipleOptions, onHide, onMoveMultipleItems, onHideMultiple, handleMoveProductDialog, rowDragTextCallback, handleToggleAddOptionModal, onCellKeyDown, onCloseBanner, onHighlightMultiple, onRowGroupOpened, toggleKits, setKitsExpansionObj, onDuplicateMultiple, handleOnRowDataUpdated, closeConfirmationDialog, onClickMultipleDelete, onDeleteBulk, toggleMoveSectionDialog, onRowDragEnd, onRowDragLeave, onRowDragMove, handleSectionNameUpdate, onCreateSectionForEmptyTable, onCheckboxChange, handleClearSelection, getTableMetaData, onCreateKitWithoutLineItems, onCreateKitWithSelectedLineItems, updateKitCreationError, handleToggleSectionModal, handleRangeSelectionChanged, handleOnPasteEnd, handleSearch, setSelectedItems, onCellContextMenu, onCellFocused, createNewRowsFromClipboard, addNewRowsToTheTable, valueGetterForMfg, suppressKeyboardEvent, companyMfgData, autoGroupColumnDef, isTableLoadingAfterMutation, isHoverCellClassRules, isAddOptionModalOpen, openMoveProductDialog, currentBodId, currentSectionId, openMoveDialog, movingIds, isLotSelectedForMovingProducts, hierarchy, openConfirmationDialog, deleteIds, addDataLoading, isCreateKitDisabled, isChangeSectionDisabled, selectedText, selectedItems, productIds, isSectionSelected, snackbarMessage, tableHeight, deleteLoading, kitCreationError, bulkActionsHideUnhideStatus, bulkActionHighLightStatus,
        showSectionModal, createKitLoading, lotMap, quickFilterText, noOfOptionSections, startColumnOnPasteRef, openBulkEditModal, handleBulkEdit, handleKitPricingCallback, bomRoundingPreset } = useBomTableActionHandlers({ bomId, tableData, tableRef, isOptionTable, getSectionBodItems, optionSection, tableMaxHeight, mainProductOptionsInfo, currentGroupWhilePastingRef, selectedRowsRef, aggregateSelectedItemsFromSections, handleKitPricingUpdate, onPricingInfoEdit, currentTabValue });

    const { columnOrderDetails } = useGetCustomisedColumnData(bomId, 'bom_documents_tab');

    const { mutateAsync: updateData } = useUpdateBomDataMutation();
    const { mutateAsync: updateMetadata } = useUpdateDatasheetMetaDataMutation();

    const showLoader = useMemo(() => (bomDataLoading || isTableLoadingAfterMutation), [bomDataLoading, isTableLoadingAfterMutation])

    useImperativeHandle(
        ref,
        () => {
            return tableRef.current;
        },
        []
    );

    useEffect(() => {
        if (productId && !showLoader) {
            const rowNode = tableRef?.current?.api?.getRowNode(productId?.toString());
            tableRef?.current?.api?.ensureIndexVisible?.((rowNode?.rowIndex || 0) - 2, 'top');
            searchParams.delete('productId');
            setSearchParams(searchParams);
        }
    }, [productId, searchParams, setSearchParams, showLoader, tableData]);

    useEffect(() => {
        setKitsExpansionObj(JSON.parse(localStorage.getItem(`${bomId}-kits`) || '{}'));
    }, [bomId, setKitsExpansionObj]);

    useLayoutEffect(() => {
        if (!tableRef.current) {
            return;
        }
        const { api } = tableRef.current;
        let overlayTimeout: NodeJS.Timeout | null = null;

        if (showLoader) {
            api?.showLoadingOverlay();
        } else if (!showLoader && tableData?.length === 0) {
            overlayTimeout = setTimeout(() => {
                api?.showNoRowsOverlay();
            }, 0);
        } else {
            api?.hideOverlay();
        }
        // Cleanup function to prevent memory leaks
        return () => {
            if (overlayTimeout) {
                clearTimeout(overlayTimeout);
            }
            // Hide overlays if component unmounts or dependencies change
            api?.hideOverlay();
        };
    }, [showLoader, tableData, tableRef]);

    const editBomData = useCallback(async ({ data, column, newValue, colDef, node }: any) => {
        const fieldName = colDef?.field || column.colId;
        const value = newValue ? (typeof newValue === 'string' ? newValue.trim().toUpperCase() : newValue) : fieldName === 'qty' ? null : '';
        if (node.level === ROW_LEVEL.SECTION) {
            return handleSectionNameUpdate({ data, newValue, node })
        }
        try {
            if (fieldName === 'manufacturer') {
                const { isValid, sanitizedValue } = isValidCharactersForMfg(value)
                const manufacturerValue = isValid ? value : sanitizedValue;

                if (!isValid) {
                    node.setData({ ...data, manufacturer: sanitizedValue })
                    setSnackbarMessage(INVALID_CHARACTERS_IN_MFG_NAME)
                }

                await updateData({
                    bomId: bomId!,
                    input: { id: data.id, [fieldName]: manufacturerValue }
                });
            } else {
                await updateData({
                    bomId: bomId!,
                    input: { id: data.id, [fieldName]: value }
                });
            }

            if (node.level === ROW_LEVEL.COMPONENT && fieldName === "qty") {
                handleKitPricingUpdate({
                    onPricingInfoEdit,
                    tableRef,
                    type: 'edit'
                })
            }
        } catch (e) {
            setSnackbarMessage(someThingWentWrongMsg);
        }
    }, [bomId, handleKitPricingUpdate, handleSectionNameUpdate, onPricingInfoEdit, setSnackbarMessage, updateData])

    const editMetadata = useCallback(async (props: any) => {
        const { data, newValue } = props;
        try {
            await updateMetadata({
                bomId: bomId!,
                bodId: data.id,
                notes: newValue ? newValue?.trim().toUpperCase() : ''
            });
        } catch (e) {
            setSnackbarMessage(someThingWentWrongMsg);
        }
    }, [bomId, setSnackbarMessage, updateMetadata])

    const onCellEdit = useCallback((props: any) => {
        const { column, newValue, data, colDef } = props;
        // while doing paste operation, need to prevent the spilling of data to other sections. This event is called before the paste end event.
        if (currentGroupWhilePastingRef.current && currentGroupWhilePastingRef.current !== data.groupPath[0]) {
            return;
        }
        const fieldName = colDef?.field || column.colId;
        if (fieldName === 'notes') {
            editMetadata({ data, column, newValue, colDef });
        } else {
            editBomData(props);
        }
    }, [currentGroupWhilePastingRef, editBomData, editMetadata])

    const redirectToAnnotatePage = useCallback((id: number, docType: string, type: string) => {
        setRouteToLocalStorage(getCancelButtonUrl(projectId, bomId, 'documents', `${id}`));
        const annotateLink = `/v2/project/${projectId}/bom/${bomId}/product/${id}/${docType}/${type}/annotate`;
        navigate(annotateLink);
    }, [bomId, navigate, projectId])

    const redirectToMatchPage = useCallback((id: number, docType: string, type: string) => {
        setRouteToLocalStorage(getCancelButtonUrl(projectId, bomId, 'documents', `${id}`));
        const matchLink = `/v2/project/${projectId}/bom/${bomId}/product/${id}/${docType}/${type}/match`;
        navigate(matchLink);
    }, [bomId, navigate, projectId])

    const showIsSelected = useCallback((args: RedirectionProps) => {
        const { selectionStatus, id, type, docType, annotationStatus, isPagesHidden, level } = args;
        if (level) return;

        if (selectionStatus === 'CONFIRMED') {
            if (annotationStatus === 'CONFIRMED') {
                return (
                    <Box onClick={() => redirectToAnnotatePage(id, docType, type)} sx={{ cursor: 'pointer' }}>
                        <Status color="success" sx={{ borderRadius: '6px' }}>
                            <BodyXS color="inherit">Annotated</BodyXS>
                        </Status>
                    </Box>
                );
            }
            if (annotationStatus === 'IN_PROGRESS') {
                return (
                    <Tooltip title="There are unconfirmed annotations on this document.">
                        <Box onClick={() => redirectToAnnotatePage(id, docType, type)} sx={{ cursor: 'pointer' }}>
                            <Status color="tertiary" sx={{ borderRadius: '6px' }}>
                                <BodyXS color="inherit">In Progress</BodyXS>
                            </Status>
                        </Box>
                    </Tooltip>
                );
            }
            return (
                <Box onClick={() => redirectToAnnotatePage(id, docType, type)} sx={{ cursor: 'pointer' }}>
                    <Status color={isPagesHidden ? 'tertiary' : 'info'} sx={{ borderRadius: '6px' }}>
                        <Box display="flex" alignItems="center" gap={1}>
                            {isPagesHidden && <ErrorIcon fontSize="small" />}
                            <BodyXS color="inherit">{isPagesHidden ? 'No Pages' : 'Selected'}</BodyXS>
                        </Box>
                    </Status>
                </Box>
            );
        }
        return (
            <Box onClick={() => redirectToMatchPage(id, docType, type)} sx={{ cursor: 'pointer' }}>
                <Status color="default" sx={{ borderRadius: '6px', backgroundColor: 'secondary.contrastText' }}>
                    <Box display="flex" alignItems="center" gap={1}>
                        <AddIcon fontSize="small" />
                        <BodyXS color="inherit">Add</BodyXS>
                    </Box>
                </Status>
            </Box>
        );
    }, [redirectToAnnotatePage, redirectToMatchPage]);

    const getDatasheetInfo = useCallback((props: ICellRendererParams) => {
        const level = props.node.level === 0;
        const { datasheet, all_pages_hidden } = props?.data || {};
        const { document_selection_status, annotations_status } = datasheet || {};

        return showIsSelected({
            selectionStatus: document_selection_status,
            id: props?.data?.id,
            docType: 'submittal',
            type: 'datasheet',
            annotationStatus: annotations_status,
            isPagesHidden: all_pages_hidden,
            level
        });
    }, [showIsSelected]);

    const getInstallationInfo = useCallback((props: ICellRendererParams) => {
        const level = props.node.level === 0;
        const { document_selection_status, annotations_status } = props?.data?.installation_guide || {};
        return showIsSelected({ selectionStatus: document_selection_status, id: props?.data?.id, docType: 'o_m', type: 'installation_guide', annotationStatus: annotations_status, level });
    }, [showIsSelected]);

    const getDrawingInfo = useCallback((props: ICellRendererParams) => {
        const level = props.node.level === 0;
        const { document_selection_status, annotations_status } = props?.data?.drawing || {};
        return showIsSelected({ selectionStatus: document_selection_status, id: props?.data?.id, docType: 'o_m', type: 'drawing', annotationStatus: annotations_status, level });
    }, [showIsSelected]);

    const getWarrantyInfo = useCallback((props: ICellRendererParams) => {
        const level = props.node.level === 0;
        const { document_selection_status, annotations_status } = props?.data?.warranty || {};
        return showIsSelected({ selectionStatus: document_selection_status, id: props?.data?.id, docType: 'o_m', type: 'warranty', annotationStatus: annotations_status, level });
    }, [showIsSelected]);

    const getAdditionalInfo = useCallback((props: ICellRendererParams) => {
        const level = props.node.level === 0;
        const { document_selection_status, annotations_status } = props?.data?.other_document || {};
        return showIsSelected({ selectionStatus: document_selection_status, id: props?.data?.id, docType: 'o_m', type: 'other_document', annotationStatus: annotations_status, level });
    }, [showIsSelected]);

    const defaultLockColumnDef: ColDef[] = useMemo(() => ([
        {
            field: BOM_TABLES_ENUM.ID,
            hide: true,
            lockPosition: 'left'
        },
        { rowDrag: true, width: 10, maxWidth: 50, resizable: false, hide: false, lockPosition: 'left', pinned: "left", editable: false },
        {
            headerComponent: () => (
                <Box mt={1}>
                    <TableHeaderMenuIcon color="secondary" fontSize="medium" />
                </Box>
            ),
            minWidth: 50,
            maxWidth: 50,
            colId: 'menu',
            resizable: false,
            cellRenderer: MenuOptions,
            cellStyle: { backgroundColor: 'unset' },
            pinned: 'right',
        }
    ]), []);

    const defaultColumnDef: Record<string, ColDef> = useMemo(() => ({
        [BOM_TABLES_ENUM.TYPE]: {
            headerName: 'Type',
            field: BOM_TABLES_ENUM.TYPE,
            minWidth: 80,
            editable: true,
            pinned: 'left',
            cellEditorParams: (params: ICellRendererParams) => {
                if (params?.node?.level === ROW_LEVEL.SECTION) {
                    return { maxLength: 90 }
                }
                return undefined;
            },
            cellEditor: 'agTextCellEditor',
            cellClassRules: { ...isHoverCellClassRules },
        },
        [BOM_TABLES_ENUM.QTY]: {
            field: BOM_TABLES_ENUM.QTY,
            headerName: 'Qty',
            cellEditor: 'agNumberCellEditor',
            cellEditorParams: {
                precision: 0,
                max: 2147483647,
                preventStepping: true
            },
            type: 'rightAligned',
            minWidth: 80,
            editable: isSectionEditingNotAllowed,
            cellClassRules: { ...isHoverCellClassRules, ...isDisabledCellClassRules },
            aggFunc: 'addQtySum',
            pinned: 'left',
            cellRenderer: QtyTemplate,
            cellDataType: 'number',

        },
        [BOM_TABLES_ENUM.MANUFACTURER]: {
            field: BOM_TABLES_ENUM.MANUFACTURER,
            headerName: 'Manufacturer',
            editable: isSectionEditingNotAllowed,
            minWidth: 80,
            pinned: 'left',
            cellEditor: AutocompleteEditor,
            valueFormatter: 'value.toUpperCase()',
            suppressKeyboardEvent: suppressKeyboardEvent,
            cellClassRules: {
                ...isHoverCellClassRules, ...isDisabledCellClassRules
            },
            valueGetter: valueGetterForMfg,
            cellEditorParams: {
                options: companyMfgData?.data.map((str) => {
                    return { label: str.manufacturer_name, value: str.manufacturer_name };
                }),
                freeSolo: true,
                values: companyMfgData?.data,
                onValueChange: onCellEdit,
                filterList: true,
                searchType: 'matchAny',
                allowTyping: true,
                highlightMatch: true,
                cellDataType: 'text',
                valueListMaxHeight: 100,
                valuePlaceholder: 'Search with Manufacture'
            } as IRichCellEditorParams
        },
        [BOM_TABLES_ENUM.MODEL_NO]: {
            field: BOM_TABLES_ENUM.MODEL_NO,
            headerName: 'Model Number',
            // editable: true,
            minWidth: 80,
            valueFormatter: 'value.toUpperCase()',
            cellEditorParams: TextCellParams,
            cellDataType: 'text',
            editable: isSectionEditingNotAllowed,
            cellClassRules: { ...isHoverCellClassRules, ...isDisabledCellClassRules },
            pinned: 'left',
        },
        [BOM_TABLES_ENUM.NOTES]: {
            field: BOM_TABLES_ENUM.NOTES,
            headerName: 'Submittal Notes (External)',
            // editable: true,
            minWidth: 80,
            cellEditorParams: TextCellParams,
            cellDataType: 'text',
            editable: isSectionEditingNotAllowed,
            cellClassRules: { ...isHoverCellClassRules, ...isDisabledCellClassRules },
        },
        [BOM_TABLES_ENUM.DATASHEET]: {
            colId: BOM_TABLES_ENUM.DATASHEET,
            headerName: 'Datasheet',
            editable: false,
            minWidth: 116,
            maxWidth: 130,
            cellRenderer: getDatasheetInfo,
            cellStyle: { textOverflow: 'unset' },
            cellClass: 'grid-cell-centered',
            cellClassRules: { ...isHoverCellClassRules, ...isDisabledCellClassRules },
        },
        [BOM_TABLES_ENUM.INSTALLATION_GUIDE]: {
            colId: BOM_TABLES_ENUM.INSTALLATION_GUIDE,
            headerName: 'Installation Guide',
            editable: false,
            minWidth: 116,
            maxWidth: 130,
            cellRenderer: getInstallationInfo,
            cellStyle: { textOverflow: 'unset' },
            cellClass: 'grid-cell-centered',
            cellClassRules: { ...isHoverCellClassRules, ...isDisabledCellClassRules },
        },
        [BOM_TABLES_ENUM.DRAWING]: {
            colId: BOM_TABLES_ENUM.DRAWING,
            headerName: 'Drawing',
            editable: false,
            minWidth: 116,
            maxWidth: 130,
            cellRenderer: getDrawingInfo,
            cellStyle: { textOverflow: 'unset' },
            cellClass: 'grid-cell-centered',
            cellClassRules: { ...isHoverCellClassRules, ...isDisabledCellClassRules },
        },
        [BOM_TABLES_ENUM.WARRANTY]: {
            colId: BOM_TABLES_ENUM.WARRANTY,
            headerName: 'Warranty',
            editable: false,
            minWidth: 116,
            maxWidth: 130,
            cellRenderer: getWarrantyInfo,
            cellStyle: { textOverflow: 'unset' },
            cellClass: 'grid-cell-centered',
            cellClassRules: { ...isHoverCellClassRules, ...isDisabledCellClassRules },
        },
        [BOM_TABLES_ENUM.OTHER_DOC]: {
            colId: BOM_TABLES_ENUM.OTHER_DOC,
            headerName: 'Other Document',
            editable: false,
            minWidth: 116,
            maxWidth: 130,
            cellRenderer: getAdditionalInfo,
            cellStyle: { textOverflow: 'unset' },
            cellClass: 'grid-cell-centered',
            cellClassRules: { ...isHoverCellClassRules, ...isDisabledCellClassRules },
        }
    }), [companyMfgData?.data, getAdditionalInfo, getDatasheetInfo, getDrawingInfo, getInstallationInfo, getWarrantyInfo, isHoverCellClassRules, onCellEdit, suppressKeyboardEvent, valueGetterForMfg])

    const columnDefs = useMemo<ColDef[]>(() => {
        const orderedColumns = columnOrderDetails?.order.reduce((result: ColDef[], field: string) => {
            field = field === "installationGuide" ? "installation_guide" : field === "otherDocument" ? "other_document" : field;
            if (Object.hasOwn(defaultColumnDef, field)) {
                const column = {
                    ...defaultColumnDef[field]
                };

                if (columnOrderDetails?.width) {
                    const widthField = field as keyof typeof columnOrderDetails.width;
                    column.width = columnOrderDetails?.width[widthField];
                }

                result.push(column);
            }

            return result;
        }, []);

        return [...defaultLockColumnDef, ...(orderedColumns || [])];
    }, [columnOrderDetails, defaultLockColumnDef, defaultColumnDef]);

    const handleColumnMoved = useCallback(async (e: ColumnMovedEvent) => {
        const newColumnOrder = e.api.getColumnState().map((column: any) => column.colId);
        const exceptDefaultColumns = newColumnOrder.slice(defaultLockColumnDef.length);
        handleTablesReordering?.(exceptDefaultColumns);
        const newArraySet = new Set(exceptDefaultColumns);
        const sortedArray: string[] = [...exceptDefaultColumns];
        if (columnOrderDetails && columnOrderDetails.order) {
            sortedArray.push(...columnOrderDetails.order.filter((item: string) => !newArraySet.has(item)));
        }
        if (e.finished && e.source === 'uiColumnMoved') {
            await saveColumnOrderEvents('bom_documents_tab', sortedArray);
        }
    }, [columnOrderDetails, defaultLockColumnDef.length, handleTablesReordering])

    const handleColumnResized = useCallback(async (props: ColumnResizedEvent) => {
        const colId = props?.column?.getColId() as string;
        const newChangedWidth = props?.column?.getActualWidth();
        const currentWidths = columnOrderDetails?.width;
        const columnState = props.api.getColumnState();
        const ignorePinnedColumnState = columnState.slice(defaultLockColumnDef.length - 1);
        const columnWidths = ignorePinnedColumnState.reduce((acc: any, item) => {
            acc[item.colId] = item.width;
            return acc;
        }, {});
        const newWidths = { ...currentWidths, ...columnWidths };
        handleTablesWidthChanged?.(colId, newChangedWidth);
        if (props.finished && props.source === 'uiColumnResized') {
            await saveColumnWidthEvents(newWidths, 'bom_documents_tab');
        }
    }, [columnOrderDetails?.width, defaultLockColumnDef.length, handleTablesWidthChanged])

    const handleProcessCellPasteForQty = useCallback((args: ProcessCellForExportParams) => {
        const { column, value } = args;
        const fieldName = column.getColId();

        if (!startColumnOnPasteRef?.current) {
            // While pasting contents from clippoard, I need to track the first column on which the cell edit callback will be fired.
            startColumnOnPasteRef.current = fieldName;
        }

        if (fieldName === 'manufacturer') {
            const { isValid, sanitizedValue } = isValidCharactersForMfg(value?.trim())
            if (!isValid) {
                setSnackbarMessage(INVALID_CHARACTERS_IN_MFG_NAME)
                return sanitizedValue
            }
        }

        if (fieldName === 'qty') {
            if (value !== null && value !== '' && !isNaN(value as unknown as number)) {
                return Number(value);
            } else {
                return null;
            }
        }
        return value;
    }, [setSnackbarMessage, startColumnOnPasteRef])

    const getDataPath = useCallback((data: SectionAndBomItemData | null): string[] => {
        return (data?.groupPath || []).map(String);
    }, []);

    const context = useMemo(() => {
        return {
            isOptionTable,
            bomId,
            lotMap,
            onAddBelow,
            onClickMoveSection,
            onDuplicate,
            onHide,
            onClickDelete,
            handleAddOption: onAddOption,
            approveOption,
            noOfOptionSections,
            onProductMoveIn: onProductMoveToKit,
            onProductMoveOut: onProductMoveOutFromKit,
            onHighlight,
            onCopyProductDataToKit,
            onCheckboxChange,
            setSelectedItems,
            selectedItems,
            disabledToolBarButton: selectedItems.length === 0,
            onCreateKit: onCreateKitWithSelectedLineItems,
            onHideMultiple,
            onMove: onMoveMultipleItems,
            onClickMultipleDelete,
            onDuplicateMultiple,
            onHighlightMultiple,
            isCreateKitDisabled,
            isChangeSectionDisabled,
            isHighlightDisabled: !(productIds || []).length,
            tableRef,
            selectedRowsRef,
            createNewRowsFromClipboard,
            addNewRowsToTheTable
        };
    }, [addNewRowsToTheTable, approveOption, bomId, createNewRowsFromClipboard, isChangeSectionDisabled, isCreateKitDisabled, isOptionTable, lotMap, noOfOptionSections, onAddBelow, onAddOption, onCheckboxChange, onClickDelete, onClickMoveSection, onClickMultipleDelete, onCopyProductDataToKit, onCreateKitWithSelectedLineItems, onDuplicate, onDuplicateMultiple, onHide, onHideMultiple, onHighlight, onHighlightMultiple, onMoveMultipleItems, onProductMoveOutFromKit, onProductMoveToKit, productIds, selectedItems, selectedRowsRef, setSelectedItems]);

    const noRowsOverlayComponent = useCallback((props: CustomNoRowsOverlayProps) => {
        // It's safe to use the old overlay component here, as the options table won't render if it's empty.
        if (!showLoader && tableData.length === 0) {
            return <NoRowsOverlayWithButtons {...props} onEnterManually={onCreateSectionForEmptyTable} />
        }

        return <LoadingOverlayComponent />
    }, [onCreateSectionForEmptyTable, showLoader, tableData.length]);

    const popupParent = useMemo<HTMLElement | null>(() => {
        return document.getElementById("root");
    }, []);

    const tableOption = useMemo(() => {
        return (props: GetContextMenuItemsParams) => getTableRightClickOptions({ ...props, context: context });
    }, [context]);

    return (
        <>
            <Portal>
                <Snackbar open={Boolean(snackbarMessage)} onClose={() => setSnackbarMessage('')} message={snackbarMessage} />
            </Portal>
            <MenuOptionsContext.Provider
                value={{
                    noOfOptionSections,
                    isOptionTable,
                    handleAddOption: onAddOption,
                    onClickMoveSection,
                    onHide,
                    onDuplicate,
                    approveOption,
                    onClickDelete,
                    bomId,
                    onAddBelow,
                    onProductMoveIn: onProductMoveToKit,
                    onProductMoveOut: onProductMoveOutFromKit,
                    onHighlight,
                    onCopyProductDataToKit,
                    onAddKitLine,
                    lotMap
                }}
            >
                <TableContext.Provider value={{ isOptionTable, setSnackbarMessage }}>
                    <Box zIndex={1} width={'100%'} height={'100%'} position={'relative'}>
                        <CustomToolBarPanel
                            toolBarOptions={toolBarOptions as ToolbarType}
                            selectedRowCount={selectedItems.length}
                            disabledToolBarButton={!selectedItems.length}
                            onDelete={onClickMultipleDelete}
                            onHideUnhide={onHideMultiple}
                            onAddDuplicates={onDuplicateMultiple}
                            onMove={onMoveMultipleItems}
                            onCreateKit={onCreateKitWithSelectedLineItems}
                            onCloseBanner={onCloseBanner}
                            onTextSearch={handleSearch}
                            onHighlight={onHighlightMultiple}
                            onCreateSection={onCreateSectionForEmptyTable}
                            isCreateKitDisabled={isCreateKitDisabled}
                            isChangeSectionDisabled={isChangeSectionDisabled}
                            selectedText={selectedText || ''}
                            onBulkEdit={handleBulkEdit}
                            isHighlightDisabled={!(productIds || []).length}
                            showBorder
                            bulkActionsHideUnhideStatus={bulkActionsHideUnhideStatus}
                            bulkActionHighLightStatus={bulkActionHighLightStatus}
                        />
                        <Box style={{ height: `${tableHeight || 317}px` }} className="ag-theme-quartz">
                            <AgGridReact
                                ref={tableRef}
                                rowData={tableData}
                                rowHeight={ROW_HEIGHT}
                                defaultColDef={defaultColDef}
                                getRowId={getRowId}
                                gridOptions={{
                                    enableRangeSelection: true,
                                    enableFillHandle: true,
                                    undoRedoCellEditing: true,
                                    enterNavigatesVerticallyAfterEdit: true,
                                    enterNavigatesVertically: true,
                                    suppressRowClickSelection: true,
                                    // suppressContextMenu: true,
                                    stopEditingWhenCellsLoseFocus: true,
                                    allowContextMenuWithControlKey: true,
                                    suppressMenuHide: false,
                                    suppressDragLeaveHidesColumns: true,
                                    rowClassRules: {
                                        'row-hide': (params: RowClassParams) => params?.data?.is_hidden,
                                        'e-highlightscroll': (params: RowClassParams) => (productId && params?.data?.id === productId ? true : false),
                                        'row-highlight': (params: RowClassParams) => (params?.data?.is_kit || Boolean(params?.data?.kit) ? false : params?.data?.is_highlighted),
                                        'e-rowhighlight-highlighscroll': (params: RowClassParams) => (productId && params?.data?.id === productId && params?.data?.is_highlighted ? true : false),
                                        'section-row-bold': (param: RowClassParams) => param.node.level === ROW_LEVEL.SECTION
                                    },
                                    aggFuncs: aggFunctions
                                }}
                                context={context}
                                columnDefs={columnDefs}
                                modules={modules}
                                onRowDragEnd={onRowDragEnd}
                                rowSelection="multiple"
                                rowDragMultiRow={true}
                                onSelectionChanged={onCheckboxChange}
                                onCellValueChanged={onCellEdit}
                                quickFilterText={quickFilterText}
                                loadingOverlayComponent={LoadingOverlayComponent}
                                processCellFromClipboard={handleProcessCellPasteForQty}
                                noRowsOverlayComponent={noRowsOverlayComponent}
                                reactiveCustomComponents
                                detailRowAutoHeight
                                onRowGroupOpened={onRowGroupOpened}
                                onColumnMoved={handleColumnMoved}
                                onColumnResized={handleColumnResized}
                                onCellKeyDown={onCellKeyDown}
                                onFirstDataRendered={toggleKits}
                                onRangeSelectionChanged={handleRangeSelectionChanged}
                                onPasteEnd={handleOnPasteEnd}
                                autoGroupColumnDef={autoGroupColumnDef}
                                treeData={true}
                                groupDefaultExpanded={-1}
                                enableGroupEdit={true}
                                getContextMenuItems={tableOption}
                                groupSuppressBlankHeader={true}
                                groupSelectsChildren={true}
                                getDataPath={getDataPath}
                                rowDragText={rowDragTextCallback}
                                onRowDragMove={onRowDragMove}
                                onRowDragLeave={onRowDragLeave}
                                onRowDataUpdated={handleOnRowDataUpdated}
                                popupParent={popupParent}
                                onCellContextMenu={onCellContextMenu}
                                onCellFocused={onCellFocused}
                            />
                        </Box>
                    </Box >
                </TableContext.Provider >
            </MenuOptionsContext.Provider >
            {
                openConfirmationDialog && (
                    <DeleteConfirmationDialog
                        title={isSectionSelected ? DELETE_SECTION_TITLE : deleteDialogTitle}
                        open={openConfirmationDialog}
                        handleDialog={closeConfirmationDialog}
                        confirmAction={onDeleteBulk}
                        loading={deleteLoading}
                    >
                        <Box width="40vw" my={4}>
                            <BodySmall limit={false} fontWeight="600" color="text.primary">
                                {deleteTitle(isSectionSelected)}
                            </BodySmall >
                            <BodySmall limit={false} my={1} color="text.primary">
                                <p style={{ whiteSpace: 'pre-line' }}>{deleteMessage(mainProductOptionsInfo!, deleteIds, isSectionSelected)}</p>
                            </BodySmall>
                        </Box >
                    </DeleteConfirmationDialog >
                )
            }
            {isAddOptionModalOpen && <AddOptionModal open={isAddOptionModalOpen} onConfirm={handleAddOptionForMultipleOptions} onClose={handleToggleAddOptionModal} isAddOptionInProgress={addDataLoading} />}
            {
                openMoveDialog && (
                    <MoveSectionDialog
                        open={openMoveDialog}
                        handleDialog={toggleMoveSectionDialog}
                        movingIds={movingIds}
                        handleClearSelection={handleClearSelection}
                        currentSectionId={currentSectionId}
                        isLotsPresentInBods={isLotSelectedForMovingProducts}
                        mainProductOptionsInfo={mainProductOptionsInfo}
                        isOptionTable={isOptionTable}
                        hierarchy={hierarchy}
                        setSnackbarMessage={setSnackbarMessage}
                        getSectionBodItems={getSectionBodItems}
                    />
                )
            }

            {
                openMoveProductDialog && currentBodId && currentSectionId && (
                    <MoveProductToKitDialog
                        open={openMoveProductDialog}
                        handleClose={handleMoveProductDialog}
                        currentBodId={currentBodId}
                        getTableMetaData={getTableMetaData}
                        sectionId={currentSectionId}
                        setSnackbarOpen={setSnackbarMessage}
                        isOptionTable={isOptionTable}
                        onPricingInfoEdit={onPricingInfoEdit}
                    />
                )
            }
            {
                kitCreationError && (
                    <ErrorDialog
                        open={Boolean(kitCreationError)}
                        handleDialog={() => updateKitCreationError(undefined)}
                        title="Unable to Create Kit"
                        text={KIT_ERROR[kitCreationError].text}
                        helperText={KIT_ERROR[kitCreationError].helperText}
                    />
                )
            }
            {showSectionModal && <SelectSectionModal open={showSectionModal} onClose={handleToggleSectionModal} isOptionTable={isOptionTable} title='Create Kit' subTitle='Select the section that you would like to create your kit in.' onSubmit={onCreateKitWithoutLineItems} isLoading={createKitLoading} />}
            {
                openBulkEditModal && (
                    <BulkEditModal
                        open={openBulkEditModal}
                        onClose={handleBulkEdit}
                        isOptionsBulkEdit={isOptionTable}
                        selectedItems={selectedItems}
                        handleClearSelection={handleClearSelection}
                        setSnackbarOpen={setSnackbarMessage}
                        rounding={bomRoundingPreset?.data?.rounding_precision || ''}
                        roundingFieldName={bomRoundingPreset?.data?.rounding_behaviour || ''}
                        options={BULK_EDIT_OPTIONS as EditOptionsT[]}
                        updateKitCostCallback={handleKitPricingCallback}
                    />
                )
            }
        </>
    );
});

export default DocumentTable;